import React, { useEffect, useState } from "react";
import { Typography, Button, Stack } from "@mui/material";
import { FruitifyContainer } from "../../../shared-components/Containers";
import { useNavigate, useParams } from "react-router-dom";
import { useApiClient } from "../../../api";
import GeneralLoading from "../../../shared-components/Loading";
import Grid from "@mui/material/Grid";
import { AssignStudentDialog } from "../components/AssignStudentDialog";
import { AssignTeacherDialog } from "../components/AssignTeacherDialog";
import { BulkClassUploadDialog } from "../components/BulkClassUploadDialog";
import { ClassViewSummary } from "../../school/routes/ClassView";
import { TeacherSummary } from "../components/FruitifyTeacherSearch";
import { FruitifyStudentSearch } from "../components/FruitifyStudentSearch";
import { checkHasApprovedSubscription } from "../../auth/Utils";
import { CreateStudentDialog } from "../components/CreateStudentDialog";

function ClassViewAdmin({ allowSwitch, adminView, setAdminView }) {
  const { id } = useParams();
  const ApiClient = useApiClient();
  const hasApprovedSubscription = checkHasApprovedSubscription();
  const navigate = useNavigate();
  const [schoolClass, setSchoolClass] = useState({});
  const [allPossibleStudents, setAllPossibleStudents] = useState([]);
  const [allPossibleTeachers, setAllPossibleTeachers] = useState([]);
  const [assignStudentDialogOpen, setAssignStudentDialogOpen] = useState(false);
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);
  const [createStudentDialogOpen, setCreateStudentDialogOpen] = useState(false);
  const [assignTeacherDialogOpen, setAssignTeacherDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchSchoolClass();
  }, [id]);

  const fetchSchoolClass = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await ApiClient.schools.getSchoolClassData({
        schoolClass: id,
      });
      if (response) {
        setSchoolClass(response.schoolClass);
        setAllPossibleStudents(response.possibleStudents);
        setAllPossibleTeachers(response.possibleTeachers);
      }
    } catch (error) {
      console.error("Failed to fetch school class", error);
      setError(String(error));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveTeacher = async (teacher) => {
    try {
      const response = await ApiClient.schools.removeTeachersFromClass({
        teachers: [teacher.id],
        schoolClass: schoolClass.id,
      });
      if (response.success) {
        setSchoolClass((prev) => ({
          ...prev,
          teachers: prev.teachers.filter((t) => t.id !== teacher.id),
        }));
      } else {
        console.error("Failed to assign teacher");
      }
    } catch (error) {
      console.error("Failed to assign teacher", error);
    }
  };

  const handleRemoveStudent = async (student) => {
    try {
      const response = await ApiClient.schools.removeStudentsFromClass({
        students: [student.id],
        schoolClass: schoolClass?.id,
      });
      if (response.success) {
        setSchoolClass((prev) => ({
          ...prev,
          students: prev.students.filter((s) => s.id !== student.id),
        }));
      } else {
        console.log("Failed to remove student");
      }
    } catch (error) {
      console.error("Failed to remove student", error);
    }
  };

  if (loading) {
    return <GeneralLoading />;
  }

  if (!Object.keys(schoolClass).length) {
    return <Typography>Unable to load School Class data. {error}</Typography>;
  }

  return (
    <FruitifyContainer>
      <CreateStudentDialog
        open={createStudentDialogOpen}
        setOpen={setCreateStudentDialogOpen}
        schoolClass={schoolClass}
        skipRedirect
        onSuccess={fetchSchoolClass}
      />
      <BulkClassUploadDialog
        open={bulkUploadDialogOpen}
        setOpen={setBulkUploadDialogOpen}
        schoolClass={schoolClass}
        onReload={fetchSchoolClass}
      />
      <AssignStudentDialog
        open={assignStudentDialogOpen}
        setOpen={setAssignStudentDialogOpen}
        students={allPossibleStudents}
        schoolClass={schoolClass}
        onReload={fetchSchoolClass}
      />
      <AssignTeacherDialog
        open={assignTeacherDialogOpen}
        setOpen={setAssignTeacherDialogOpen}
        teachers={allPossibleTeachers}
        schoolClass={schoolClass}
        onReload={fetchSchoolClass}
      />
      <ClassViewSummary
        schoolClass={schoolClass}
        allowSwitch={allowSwitch}
        adminView={adminView}
        setAdminView={setAdminView}
      />
      {hasApprovedSubscription && (
        <>
          <Stack
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
            sx={{ mt: 2 }}
          >
            <Typography variant={"h5"}>Teachers</Typography>
            <Button
              variant="contained"
              onClick={() => setAssignTeacherDialogOpen(true)}
            >
              Assign Teacher
            </Button>
          </Stack>
          <Grid container spacing={4} direction={"row"} sx={{ mt: 2 }}>
            {schoolClass.teachers.map((teacher) => (
              <Grid key={teacher.id} size={{ xs: 12, md: 4 }}>
                <TeacherSummary
                  teacher={teacher}
                  onUnassignClicked={handleRemoveTeacher}
                  onViewClicked={(teacher) =>
                    navigate(`/management/teachers/view/${teacher.id}`)
                  }
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}
      <Grid container direction={"row"} sx={{ mt: 2 }}>
        <Stack
          sx={{ width: "100%" }}
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Typography variant="h5" fontWeight="bold">
            Students
          </Typography>
          <Stack direction={"row"} spacing={2}>
            <Button
              variant="contained"
              onClick={() => setBulkUploadDialogOpen(true)}
            >
              Bulk Upload
            </Button>
            <Button
              variant="contained"
              onClick={() => setAssignStudentDialogOpen(true)}
            >
              Assign Student
            </Button>
            <Button
              variant="contained"
              onClick={() => setCreateStudentDialogOpen(true)}
            >
              Create Student
            </Button>
          </Stack>
        </Stack>
        <FruitifyStudentSearch
          students={schoolClass?.students}
          onUnassignStudentClicked={handleRemoveStudent}
        />
      </Grid>
    </FruitifyContainer>
  );
}

export default ClassViewAdmin;
